import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { RequestStatusObject, buildReducers, createLazyList, createRequestStatusObject } from '../utils';
import { LazyList, LazyListRequest, LazyListResponse } from '../../types/types';

import { readUser } from './user';
import { enqueueSnackbar } from 'notistack';
import i18n from '../../i18n';
import { CandidateInterest, InterestStatus } from '../../types/candidateInterest';
// import { readOrganisation } from './organisation';

const initialState: {
	current: CandidateInterest | undefined;
	requestForm: Partial<CandidateInterest>; // used fo the form
	userList: LazyList<CandidateInterest>;
	organisationList: LazyList<CandidateInterest>;
	readRequest: RequestStatusObject;
	listRequest: RequestStatusObject;
	createRequest: RequestStatusObject;
	updateRequest: RequestStatusObject;
} = {
	current: undefined,
	requestForm: {},
	userList: createLazyList(),
	organisationList: createLazyList(),
	readRequest: createRequestStatusObject(),
	listRequest: createRequestStatusObject(),
	createRequest: createRequestStatusObject(),
	updateRequest: createRequestStatusObject()
};
export const readCandidateInterest = createAsyncThunk<
	CandidateInterest,
	string,
	{ dispatch: AppDispatch; state: RootState }
>(
	'candidateInterest/read',

	async (id: string, { dispatch, getState }) => {
		// current user Id

		const result = await API.get('inspirusapi', `/candidate-interests/${id}`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});

		const interest: CandidateInterest = result.data;

		// const organisation = getState().organisation.current;
		// if (organisation?.id !== interest.organisationId) {
		// 	dispatch(readOrganisation(interest.organisationId));
		// }

		// request of another user
		const user = getState().user.pageUser;
		if (user?.id !== interest.userId) {
			dispatch(readUser({ id: interest.userId }));
		}

		return result.data;
	}
);

export const listUserInterests = createAsyncThunk<
	LazyList<CandidateInterest>,
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>(
	'candidateInterest/list',

	async () => {
		const result = await API.get('inspirusapi', '/candidate-interests', {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		const list = {
			totalCount: result.data.length,
			items: result.data
		};
		return list;
	}
);

export const listOrganisationInterests = createAsyncThunk<
	LazyListResponse<CandidateInterest>,
	{
		organisationId: string;
		roleName: string;
		status: InterestStatus;
		advertId?: string;
		lastLoggedOut?: string;
		dailyUpdates?: boolean;
	} & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'candidateInterest/listOrganisation',

	async ({ organisationId, limit, next, roleName, status, advertId, lastLoggedOut, dailyUpdates = false }) => {
		// current user Id
		const result = await API.get('inspirusapi', `/organisations/${organisationId}/candidate-interests`, {
			headers: {},
			response: true,
			queryStringParameters: {
				limit,
				next,
				roleName,
				status,
				spotlightId: advertId,
				lastLoggedOut,
				dailyUpdates
			}
		});

		return { ...result.data };
	}
);

export const createCandidateInterest = createAsyncThunk<
	CandidateInterest,
	Partial<CandidateInterest>,
	{ dispatch: AppDispatch; state: RootState }
>(
	'candidateInterest/create',

	async (values) => {
		// current user Id

		const result = await API.post('inspirusapi', '/candidate-interests/', {
			headers: {},
			response: true,
			body: values
		});

		return result.data;
	}
);

export const updateCandidateInterest = createAsyncThunk<
	CandidateInterest,
	{ id: string; status?: InterestStatus; sharedDocumentId?: string; note?: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'candidateInterest/update',

	async ({ id, status, sharedDocumentId, note }) => {
		// current user Id

		const result = await API.put('inspirusapi', `/candidate-interests/${id}`, {
			headers: {},
			response: true,
			body: {
				status,
				sharedDocumentId,
				note
			}
		});

		return result.data;
	}
);

const candidateInterestSlice = createSlice({
	name: 'candidateInterest',
	initialState,
	reducers: {
		resetVerification: (state) => {
			state = initialState;
			return state;
		},
		setCurrentCandidateInterestForm: (state, action) => {
			const candidateInterest = action.payload as Partial<CandidateInterest>;
			state.requestForm = {
				...state.requestForm,
				...candidateInterest
			};
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, readCandidateInterest, 'readRequest', {
			onFulfilled: (state, payload) => {
				state.current = payload;
			}
		});
		buildReducers(builder, listUserInterests, 'listRequest', {
			onFulfilled: (state, payload) => {
				state.userList = payload;
			}
		});
		buildReducers(builder, listOrganisationInterests, 'listRequest', {
			onFulfilled: (state, payload) => {
				const list = state.organisationList;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			}
		});

		buildReducers(builder, createCandidateInterest, 'createRequest', {
			onFulfilled: (state) => {
				enqueueSnackbar(i18n.t('pages.candidateInterest.request.successMessage'), { variant: 'success' });
				state.requestForm = {};
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.candidateInterest.request.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, updateCandidateInterest, 'updateRequest', {
			onFulfilled: (state) => {
				enqueueSnackbar(i18n.t('pages.candidateInterest.update.successMessage'), { variant: 'success' });
				state.requestForm = {};
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.candidateInterest.request.errorMessage'), { variant: 'error' });
			}
		});
	}
});

export const { resetVerification, setCurrentCandidateInterestForm } = candidateInterestSlice.actions;
export default candidateInterestSlice.reducer;
